import { makeStyles } from '@map/theme';
import { Dialog, DialogProps, Typography } from '@mui/material';
import { getIn } from '@shared/util/object';
import { cloneWithClassName } from '@shared/util/react';
import Button from '@ui/Button';
import CloseIcon from '@ui/Icon/library/CloseIcon';
import type SVGIllustration from '@ui/SVGIllustration';
import { ReactElement, ReactNode, type ComponentProps } from 'react';

const useStyles = makeStyles(({ borderRadius, spacing }, { compact }: { compact: boolean }) => ({
  dialog: {
    margin: spacing(4),
    padding: spacing(2),
    borderRadius: 24,
  },
  top: {
    padding: compact ? spacing(4, 0, 0, 0) : spacing(8, 0),
    borderRadius: borderRadius(20, 20, 0, 0),
  },
  illustration: {
    display: 'block',
    margin: '0 auto',
  },
  bottom: {
    padding: compact ? spacing(4, 5, 3) : spacing(5, 5, 3),
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    margin: compact ? spacing(4, 0, 0) : spacing(5, 0, 0),
    '&:first-child': {
      margin: 0,
    },
    '& > *': {
      marginBottom: spacing(2),
    },
  },
  closeButton: {
    position: 'absolute',
    top: spacing(2),
    right: spacing(2),
  },
}));

type Props = {
  className?: string;
  open?: boolean;
  illustration: ReactElement<ComponentProps<typeof SVGIllustration>>;
  illustrationBackground?: string;
  illustrationClassName?: string;
  title?: ReactNode;
  description?: ReactNode;
  onClose?: () => void;
  dismissable?: boolean;
  closable?: boolean;
  children?: ReactNode;
  buttonsClassName?: string;
  compact?: boolean;
  maxWidth?: DialogProps['maxWidth'];
};

const IllustratedDialog = (props: Props) => {
  const {
    className,
    illustration,
    illustrationBackground = 'primary.100',
    illustrationClassName,
    title,
    description,
    onClose,
    open = true,
    dismissable = true,
    closable = false,
    children,
    compact = false,
    maxWidth,
    buttonsClassName,
  } = props;
  const { classes, cx, theme } = useStyles({ compact });
  return (
    <Dialog
      maxWidth={maxWidth}
      classes={{ paper: cx(classes.dialog, className) }}
      open={open}
      onClose={dismissable ? onClose : undefined}
    >
      <div
        className={cx(classes.top, illustrationClassName)}
        style={{ backgroundColor: getIn(theme.palette, illustrationBackground) }}
      >
        {cloneWithClassName(illustration, classes.illustration, cx)}
      </div>
      <div className={classes.bottom}>
        {title && (
          <Typography variant="subtitle1" align="center" gutterBottom>
            {title}
          </Typography>
        )}
        {description && (
          <Typography variant="body2" color="textSecondary" align="center" component="div">
            {description}
          </Typography>
        )}
        {children && <div className={cx(classes.buttons, buttonsClassName)}>{children}</div>}
        {closable && (
          <Button
            variant="tertiary"
            size="medium"
            className={classes.closeButton}
            onClick={onClose}
            icon={true}
          >
            <CloseIcon />
          </Button>
        )}
      </div>
    </Dialog>
  );
};

export default IllustratedDialog;
